import React, { useState, useEffect, useMemo } from 'react';
import './GenerationProgressModal.css';

interface GenerationProgressModalProps {
  isOpen: boolean;
  onClose: () => void;
  generationType: 'character' | 'background' | 'scene' | 'asset';
  itemName?: string;
  generatedImageUrl?: string | null;
  onRegenerate?: () => void;
  model?: 'chatgpt' | 'gemini';
}

const GenerationProgressModal: React.FC<GenerationProgressModalProps> = ({
  isOpen,
  onClose,
  generationType,
  itemName,
  generatedImageUrl,
  onRegenerate,
  model = 'gemini'
}) => {
  const [progress, setProgress] = useState(0);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);

  // Friendly messages for each generation type - memoized to prevent recreating on every render
  const messages = useMemo(() => ({
    character: [
      "🤖 Gathering AI to work on your character...",
      "🎨 AI is sketching your character...",
      "✨ Adding personality and charm...",
      "🌈 Coloring your character beautifully...",
      "🎭 Putting on the final touches...",
      "✨ Almost ready! Polishing the details...",
      "💾 Saving your character to storage..."
    ],
    background: [
      "🤖 Gathering AI to create your background...",
      "🎨 AI is painting the scene...",
      "🌳 Adding beautiful landscapes...",
      "🎭 Creating the perfect atmosphere...",
      "✨ Adding magical details...",
      "✨ Almost ready! Final adjustments...",
      "💾 Saving your background to storage..."
    ],
    scene: [
      "🤖 Gathering AI to compose your scene...",
      "🎨 AI is arranging the characters...",
      "🌟 Positioning elements perfectly...",
      "🎭 Creating the perfect composition...",
      "✨ Adding special effects...",
      "✨ Almost ready! Final touches...",
      "💾 Saving your scene to storage..."
    ],
    asset: [
      "🤖 Gathering AI to create your asset...",
      "🎨 AI is designing your asset...",
      "✨ Adding creative elements...",
      "🎭 Making it unique and special...",
      "🌈 Coloring with style...",
      "✨ Almost ready! Final polish...",
      "💾 Saving your asset to storage..."
    ]
  }), []);

  useEffect(() => {
    if (!isOpen) {
      setProgress(0);
      setCurrentMessageIndex(0);
      return;
    }

    // If generation completed, immediately show 100%
    if (generatedImageUrl) {
      setProgress(100);
      setCurrentMessageIndex(messages[generationType].length - 1);
      return;
    }

    // Progress animation - different durations based on model
    // Nano Banana (gemini): ~20 seconds, ChatGPT: ~90 seconds
    let totalDuration: number;
    if (model === 'gemini') {
      totalDuration = 20000; // 20s for Nano Banana
    } else {
      totalDuration = 90000; // 90s for ChatGPT
    }
    const messageDuration = totalDuration / messages[generationType].length;
    const progressIncrement = 95 / (totalDuration / 100); // Reach 95% over time

    let currentProgress = 0;
    let messageIndex = 0;

    const progressInterval = setInterval(() => {
      currentProgress += progressIncrement;
      if (currentProgress >= 95) {
        currentProgress = 95;
        clearInterval(progressInterval);
      }
      setProgress(currentProgress);
    }, 100);

    const messageInterval = setInterval(() => {
      messageIndex = messageIndex + 1;
      if (messageIndex < messages[generationType].length) {
        setCurrentMessageIndex(messageIndex);
      } else {
        // Stay at the last message once we reach it
        messageIndex = messages[generationType].length - 1;
        clearInterval(messageInterval);
      }
    }, messageDuration);

    return () => {
      clearInterval(progressInterval);
      clearInterval(messageInterval);
    };
  }, [isOpen, generationType, generatedImageUrl, messages, model]);

  if (!isOpen) return null;

  const currentMessage = messages[generationType][currentMessageIndex];
  const displayName = itemName || 'your creation';
  
  // Get estimated time based on model
  const getEstimatedTime = () => {
    if (model === 'gemini') return '~20 seconds';
    return '~90 seconds';
  };

  return (
    <div className="generation-progress-overlay">
      <div className="generation-progress-modal">
        <div className="generation-progress-header">
          <h2>{generatedImageUrl ? '✨ Generation Complete!' : 'AI Generation in Progress'}</h2>
          <p className="generation-item-name">
            {generatedImageUrl ? 'Created:' : 'Creating:'} <strong>{displayName}</strong>
          </p>
          {!generatedImageUrl && (
            <p className="generation-estimated-time" style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
              ⏱️ Estimated time: {getEstimatedTime()} {model === 'gemini' ? '(Nano Banana)' : '(ChatGPT)'}
            </p>
          )}
        </div>

        <div className="generation-progress-content">
          {generatedImageUrl ? (
            // Show completed generation with image
            <div className="generation-complete-content">
              <div className="generation-result-image">
                <img 
                  src={generatedImageUrl} 
                  alt={`Generated ${generationType}: ${displayName}`}
                  style={{ 
                    maxWidth: '100%', 
                    maxHeight: '300px', 
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                  }}
                />
              </div>
              <div className="generation-success-message">
                <p className="generation-info-text" style={{ fontSize: '16px', fontWeight: '500', color: '#28a745' }}>
                  🎉 Your {generationType} has been created successfully!
                </p>
                <p className="generation-info-text">
                  You can now find "{displayName}" in your {generationType}s list.
                </p>
              </div>
            </div>
          ) : (
            // Show progress during generation
            <>
              <div className="generation-progress-bar-container">
                <div
                  className="generation-progress-bar"
                  style={{ width: `${progress}%` }}
                />
              </div>

              <div className="generation-progress-percentage">
                {Math.round(progress)}%
              </div>

              <div className="generation-progress-message">
                <div className="generation-message-text">
                  {currentMessage}
                </div>
                <div className="generation-message-spinner">
                  <div className="spinner"></div>
                </div>
              </div>

              <div className="generation-progress-info">
                <p className="generation-info-text">
                  This usually takes 30-60 seconds. Please don't close this window.
                </p>
                <p className="generation-info-text">
                  The AI is working hard to create something amazing for you! ✨
                </p>
              </div>
            </>
          )}
        </div>

        <div className="generation-progress-actions">
          {generatedImageUrl && onRegenerate && (
            <button
              className="btn btn-secondary generation-cancel-btn"
              onClick={() => {
                onRegenerate();
                onClose();
              }}
            >
              🔄 Regenerate
            </button>
          )}
          <button
            className={`btn ${generatedImageUrl ? 'btn-primary' : 'btn-secondary'} generation-cancel-btn`}
            onClick={onClose}
            disabled={!generatedImageUrl && progress >= 95} // Disable during generation when almost done
          >
            {generatedImageUrl ? 'Continue' : (progress >= 95 ? 'Please wait...' : 'Cancel')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GenerationProgressModal;